<script setup lang="ts">
import window from '@@/bits/global'
import { cachedLandsideApp } from '@@/bits/landside_app_helper'
import { defineAsyncComponent } from '@@/bits/vue'
import { LandsidePage, useLandsideNavigationStore } from '@@/pinia/landside_navigation'
import { useQuotationFormDialogStore } from '@@/pinia/quotation_form_dialog'
import { useScreenReaderNotificationsStore } from '@@/pinia/screen_reader_notifications'
import ScreenReaderSpeechNotifications from '@@/vuecomponents/ScreenReaderSpeechNotifications.vue'
import { storeToRefs } from 'pinia'
import { onBeforeMount, onMounted, watch } from 'vue'

const LandsideHomeV2Page =
  cachedLandsideApp[LandsidePage.Home] ?? defineAsyncComponent(() => import('@@/vuecomponents/LandsideHomeV2Page.vue'))
const LandsideSandboxPage =
  cachedLandsideApp[LandsidePage.Sandbox] ??
  defineAsyncComponent(() => import('@@/vuecomponents/LandsideSandboxPage.vue'))
const LandsideProductPage =
  cachedLandsideApp[LandsidePage.Product] ??
  defineAsyncComponent(() => import('@@/vuecomponents/LandsideProductPage.vue'))
const LandsideSubscriptionPage =
  cachedLandsideApp[LandsidePage.Subscriptions] ??
  defineAsyncComponent(() => import('@@/vuecomponents/LandsideSubscriptionPage.vue'))
const LandsideTemplatePage =
  cachedLandsideApp[LandsidePage.Templates] ??
  defineAsyncComponent(() => import('@@/vuecomponents/LandsideTemplatePage.vue'))
const LandsideDynamicLandingPage =
  cachedLandsideApp[LandsidePage.DynamicLandingPage] ??
  defineAsyncComponent(() => import('@@/vuecomponents/LandsideDynamicLandingPage.vue'))
const LandsideBoardPage =
  cachedLandsideApp[LandsidePage.Board] ??
  defineAsyncComponent(() => import('@@/vuecomponents/LandsideProductHowItWorksPage.vue'))
const LandsideEducationPage =
  cachedLandsideApp[LandsidePage.Education] ??
  defineAsyncComponent(() => import('@@/vuecomponents/LandsideProductEducationPage.vue'))
const QuotationFormDialog = defineAsyncComponent(() => import('@@/vuecomponents/QuotationFormDialog.vue'))

const emit = defineEmits<{
  (event: 'loading'): void
  (event: 'loaded'): void
}>()

const landsideNavigationStore = useLandsideNavigationStore()
const screenReaderNotificationStore = useScreenReaderNotificationsStore()
const { currentPage } = storeToRefs(landsideNavigationStore)
const { isQuoteModalOpen } = storeToRefs(useQuotationFormDialogStore())

// Watch for page changes to emit loading event
watch(currentPage, () => {
  emit('loading')
})

onBeforeMount(() => {
  // We set the correct page before the component mounts
  currentPage.value = window.ww.vueStartingState.currentPage ?? currentPage.value
})

onMounted(() => {
  // We add a background image for homepage in the LandsideController.
  // Remove it once the app mounts.
  if (currentPage.value === LandsidePage.Home) {
    const htmlRootElement = document.documentElement
    htmlRootElement.style.backgroundImage = ''
  }
})
</script>

<template>
  <main id="main-content" tabindex="-1">
    <ScreenReaderSpeechNotifications :message="screenReaderNotificationStore.latestScreenReaderMessage" />
    <LandsideHomeV2Page v-if="currentPage === LandsidePage.Home" @vue:mounted="emit('loaded')" />
    <LandsideProductPage v-else-if="currentPage === LandsidePage.Product" @vue:mounted="emit('loaded')" />
    <LandsideSubscriptionPage
      v-else-if="currentPage === LandsidePage.Subscriptions || currentPage === LandsidePage.Pricing"
      @vue:mounted="emit('loaded')"
    />
    <LandsideTemplatePage v-else-if="currentPage === LandsidePage.Templates" @vue:mounted="emit('loaded')" />
    <LandsideDynamicLandingPage
      v-else-if="currentPage === LandsidePage.DynamicLandingPage"
      @vue:mounted="emit('loaded')"
    />
    <LandsideSandboxPage v-else-if="currentPage === LandsidePage.Sandbox" @vue:mounted="emit('loaded')" />
    <LandsideBoardPage v-else-if="currentPage === LandsidePage.Board" @vue:mounted="emit('loaded')" />
    <LandsideEducationPage v-else-if="currentPage === LandsidePage.Education" @vue:mounted="emit('loaded')" />
    <QuotationFormDialog v-if="isQuoteModalOpen" @vue:mounted="emit('loaded')" />
  </main>
</template>
